import { getSceneActions, getSceneState } from "../sceneStore";
import { getViewerHandles } from "../../runtime/viewerHandles";
import { setJointLabelDisplayedName } from "../../viewer/labelUtils";
import {
  indexActuatorsForJointVisualization,
  getJointCssLabelMainText,
} from "../../viewer/jointActuatorLabelUtils";
import { useViewerStore } from "../viewerStore";
import { captureCameraHome } from "../../runtime/cameraHome";
import { migrateSceneConfig } from "../../core/migrations/sceneConfig";
import { logger } from "../../utils/logger";
import { applyProjectJointMarkerScalePercent } from "../../selected-joint-visibility";
import {
  applyProjectFloorAppearanceOptions,
  FLOOR_APPEARANCE_MODES,
  FLOOR_FINISHES,
} from "../../utils/floorAppearanceOptions";
import type { FileMap } from "./types";
import type {
  Actuator,
  Body,
  BodyUUID,
  Geom,
  Joint,
  Mesh,
  Quaternion,
  SceneCamera,
  Site,
  Vector3,
  WorldBody,
} from "../../types/scene";

const VIEWPORT_EXPORT_CAMERA_NAME = "viewport";

type NamedUuidConfigEntry = {
  uuid?: unknown;
  name?: unknown;
  label?: unknown;
  mjcfName?: unknown;
};

type NamingConfig = {
  bodies?: NamedUuidConfigEntry[];
  worldBodies?: NamedUuidConfigEntry[];
  actuators?: NamedUuidConfigEntry[];
  joints?: NamedUuidConfigEntry[];
  sites?: NamedUuidConfigEntry[];
  meshes?: NamedUuidConfigEntry[];
};

type CameraConfig = {
  position?: unknown;
  quaternionXyzw?: unknown;
  zoom?: unknown;
  fov?: unknown;
  near?: unknown;
  far?: unknown;
};

type OrbitConfig = {
  target?: unknown;
};

type SavedCameraConfig = Record<string, unknown> & {
  kind?: unknown;
  mountType?: unknown;
  mountSiteUuid?: unknown;
  mountBodyUuid?: unknown;
};

type CameraProbeConfig = {
  savedCameras?: unknown;
};

type PoseBodyConfig = {
  name?: unknown;
  position?: unknown;
  orientation?: unknown;
};

type PoseJointConfig = {
  name?: unknown;
  value?: unknown;
  quat?: unknown;
};

type SceneConfigLike = {
  camera?: CameraConfig;
  orbit?: OrbitConfig;
  cameraProbe?: CameraProbeConfig;
  naming?: NamingConfig;
  geomPairings?: GeomPairingConfig[];
  visibilityOverrides?: VisibilityOverridesConfig;
  visibilitySettings?: VisibilitySettingsConfig;
  poses?: {
    init?: {
      bodies?: Record<string, PoseBodyConfig>;
      joints?: Record<string, PoseJointConfig>;
    };
  };
};

type GeomPairingConfig = {
  bodyUuid?: unknown;
  bodyIndex?: unknown;
  bodyName?: unknown;
  geomIndex?: unknown;
  pairedGeomIndex?: unknown;
};

type VisibilityOverridesConfig = {
  bodyVisibility?: unknown;
  meshVisibility?: unknown;
  collisionGeomVisibility?: unknown;
};

type VisibilitySettingsConfig = {
  jointMarkerScalePercent?: unknown;
  floorAppearance?: unknown;
};

type CssLabelObject = {
  element?: {
    className?: string;
    textContent?: string | null;
    querySelector?: (selector: string) => { textContent?: string | null } | null;
  };
  userData?: Record<string, unknown>;
  parent?: { userData?: Record<string, unknown> };
};

// Strips the "b_" prefix from UUID-like names so config lookups are consistent
// regardless of whether the stored key is "b_<uuid>" or a raw UUID.
const normalizeUuidKey = (value: unknown): string => {
  const s = String(value ?? "").trim();
  if (!s) return "";
  const m = s.match(/^b_([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i);
  if (m?.[1]) return m[1];
  const m2 = s.match(/^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i);
  if (m2?.[1]) return m2[1];
  return s;
};

const recordHasEntityKey = (
  record: Record<string, unknown> | undefined,
  rawKey: unknown
): boolean => {
  if (!record) return false;
  const key = typeof rawKey === "string" ? rawKey.trim() : "";
  if (!key) return false;
  if (record[key]) return true;
  const n = normalizeUuidKey(key);
  for (const k of Object.keys(record)) {
    if (normalizeUuidKey(k) === n && record[k]) return true;
  }
  return false;
};

export function restoreGeomPairingsFromConfig(
  geomPairings: unknown[] | undefined | null,
  source: string = "scene_config.json"
): number {
  if (!geomPairings?.length) return 0;

  const sceneActions = getSceneActions();
  const sceneState = getSceneState();
  const nextBodies: Record<string, Body> = { ...(sceneState.bodies || {}) };
  const orderedBodyUuids: string[] = [];
  const visitBody = (uuid: string) => {
    if (!uuid || orderedBodyUuids.includes(uuid) || !nextBodies[uuid]) return;
    orderedBodyUuids.push(uuid);
    const children = Array.isArray(nextBodies[uuid]?.children) ? nextBodies[uuid].children : [];
    children.forEach((childUuid) => visitBody(String(childUuid || "")));
  };
  (Array.isArray(sceneState.worldBodies) ? sceneState.worldBodies : []).forEach((wb) => {
    const uuid = typeof wb === "string" ? wb : wb?.uuid;
    visitBody(String(uuid || ""));
  });
  Object.keys(nextBodies).forEach(visitBody);

  const resolveBodyForPairing = (pairing: GeomPairingConfig) => {
    const bodyUuid = normalizeUuidKey(pairing?.bodyUuid);
    if (bodyUuid && nextBodies[bodyUuid]) return { uuid: bodyUuid, body: nextBodies[bodyUuid] };

    const bodyIndex = Number(pairing?.bodyIndex);
    if (Number.isInteger(bodyIndex) && bodyIndex >= 0 && bodyIndex < orderedBodyUuids.length) {
      const uuid = orderedBodyUuids[bodyIndex];
      if (uuid && nextBodies[uuid]) return { uuid, body: nextBodies[uuid] };
    }

    const bodyName = typeof pairing?.bodyName === "string" ? pairing.bodyName.trim() : "";
    if (bodyName) {
      const matches = orderedBodyUuids.filter((uuid) => nextBodies[uuid]?.name === bodyName);
      if (matches.length === 1) {
        const uuid = matches[0];
        return { uuid, body: nextBodies[uuid] };
      }
    }

    if (orderedBodyUuids.length === 1) {
      const uuid = orderedBodyUuids[0];
      return { uuid, body: nextBodies[uuid] };
    }

    return null;
  };
  let restoredCount = 0;

  geomPairings.forEach((rawPairing) => {
    const pairing = rawPairing as GeomPairingConfig;
    const resolved = resolveBodyForPairing(pairing);
    const bodyUuid = resolved?.uuid || "";
    const body = resolved?.body || null;
    const geoms = Array.isArray(body?.geoms) ? body.geoms : null;
    if (!body || !geoms) return;

    const indexA = Number(pairing?.geomIndex);
    const indexB = Number(pairing?.pairedGeomIndex);
    if (
      !Number.isInteger(indexA) ||
      !Number.isInteger(indexB) ||
      indexA < 0 ||
      indexB < 0 ||
      indexA >= geoms.length ||
      indexB >= geoms.length ||
      indexA === indexB
    ) {
      logger.warn(`[fileStore] Skipping invalid geom pairing from ${source}`, pairing);
      return;
    }

    const geomA = geoms[indexA];
    const geomB = geoms[indexB];
    if (!geomA?.uuid || !geomB?.uuid) return;
    const visualA = geomA.visualOnly === true;
    const visualB = geomB.visualOnly === true;
    if (visualA === visualB) {
      logger.warn("[fileStore] Skipping geom pairing without one visual and one collision geom", {
        bodyUuid,
        geomIndex: indexA,
        pairedGeomIndex: indexB,
      });
      return;
    }

    const nextGeoms = geoms.map((geom) => ({ ...geom }));
    nextGeoms[indexA].pairedGeomUUID = String(geomB.uuid) as NonNullable<Geom["pairedGeomUUID"]>;
    nextGeoms[indexB].pairedGeomUUID = String(geomA.uuid) as NonNullable<Geom["pairedGeomUUID"]>;
    nextBodies[bodyUuid] = { ...body, geoms: nextGeoms };
    restoredCount += 1;
  });

  if (restoredCount > 0) {
    sceneActions.setBodies(nextBodies, sceneState.worldBodies || []);
    try {
      window.dispatchEvent(new Event("structure-changed"));
    } catch {
      // Best-effort UI notification; the restored pairings are already applied.
    }
    logger.info(`[fileStore] Restored geom pairings from ${source}`, {
      count: restoredCount,
    });
  }

  return restoredCount;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

export function restoreVisibilityOverridesFromConfig(
  visibilityOverrides: unknown,
  source: string = "scene_config.json"
): number {
  if (!isRecord(visibilityOverrides)) return 0;

  const sceneState = getSceneState();
  const bodies = (sceneState.bodies || {}) as Record<string, Body>;
  const meshes = (sceneState.meshes || {}) as Record<string, unknown>;

  const bodyVisibility: Record<string, boolean> = {};
  const meshVisibility: Record<string, boolean> = {};
  const collisionGeomVisibility: Record<string, boolean> = {};
  let skippedCount = 0;

  const rawBodyVisibility = visibilityOverrides.bodyVisibility;
  if (isRecord(rawBodyVisibility)) {
    Object.entries(rawBodyVisibility).forEach(([rawUuid, rawVisible]) => {
      const uuid = normalizeUuidKey(rawUuid);
      if (typeof rawVisible !== "boolean" || !uuid || !bodies[uuid]) {
        skippedCount += 1;
        return;
      }
      bodyVisibility[uuid] = rawVisible;
    });
  }

  const rawMeshVisibility = visibilityOverrides.meshVisibility;
  if (isRecord(rawMeshVisibility)) {
    Object.entries(rawMeshVisibility).forEach(([rawUuid, rawVisible]) => {
      const uuid = normalizeUuidKey(rawUuid);
      if (typeof rawVisible !== "boolean" || !uuid || !meshes[uuid]) {
        skippedCount += 1;
        return;
      }
      meshVisibility[uuid] = rawVisible;
    });
  }

  const rawCollisionGeomVisibility = visibilityOverrides.collisionGeomVisibility;
  if (isRecord(rawCollisionGeomVisibility)) {
    Object.entries(rawCollisionGeomVisibility).forEach(([rawKey, rawVisible]) => {
      const [rawBodyUuid, rawGeomUuid] = String(rawKey || "").split(":");
      const bodyUuid = normalizeUuidKey(rawBodyUuid);
      const geomUuid = normalizeUuidKey(rawGeomUuid);
      const body = bodyUuid ? bodies[bodyUuid] : null;
      const hasGeom =
        !!body &&
        Array.isArray(body.geoms) &&
        body.geoms.some((geom: Geom) => String(geom?.uuid || "") === geomUuid);
      if (typeof rawVisible !== "boolean" || !bodyUuid || !geomUuid || !hasGeom) {
        skippedCount += 1;
        return;
      }
      collisionGeomVisibility[`${bodyUuid}:${geomUuid}`] = rawVisible;
    });
  }

  useViewerStore.setState({
    bodyVisibility,
    meshVisibility,
    collisionGeomVisibility,
  });

  const restoredCount =
    Object.keys(bodyVisibility).length +
    Object.keys(meshVisibility).length +
    Object.keys(collisionGeomVisibility).length;

  if (skippedCount > 0) {
    logger.warn(`[fileStore] Skipped invalid visibility overrides from ${source}`, {
      count: skippedCount,
    });
  }

  if (restoredCount > 0) {
    logger.info(`[fileStore] Restored visibility overrides from ${source}`, {
      bodyCount: Object.keys(bodyVisibility).length,
      meshCount: Object.keys(meshVisibility).length,
      collisionGeomCount: Object.keys(collisionGeomVisibility).length,
    });
  }

  return restoredCount;
}

export function restoreVisibilitySettingsFromConfig(
  visibilitySettings: unknown,
  source: string = "scene_config.json"
): number {
  if (!isRecord(visibilitySettings)) return 0;
  let restoredCount = 0;

  if ("jointMarkerScalePercent" in visibilitySettings) {
    const markerScalePercent = Number(visibilitySettings.jointMarkerScalePercent);
    if (!Number.isFinite(markerScalePercent)) {
      logger.warn(`[fileStore] Skipping invalid joint marker scale from ${source}`, {
        value: visibilitySettings.jointMarkerScalePercent,
      });
    } else {
      applyProjectJointMarkerScalePercent(markerScalePercent);
      logger.info(`[fileStore] Restored joint marker scale from ${source}`, {
        jointMarkerScalePercent: markerScalePercent,
      });
      restoredCount += 1;
    }
  }

  if ("floorAppearance" in visibilitySettings) {
    const floorAppearance = visibilitySettings.floorAppearance;
    if (!isRecord(floorAppearance)) {
      logger.warn(`[fileStore] Skipping invalid floor appearance from ${source}`, {
        value: floorAppearance,
      });
    } else {
      const patch: {
        mode?: string;
        floorColorHex?: string | null;
        floorOpacity?: number | null;
        floorFinish?: string | null;
      } = {};
      const mode = floorAppearance.mode;
      if (mode === FLOOR_APPEARANCE_MODES.light || mode === FLOOR_APPEARANCE_MODES.dark) {
        patch.mode = String(mode);
      } else if ("mode" in floorAppearance) {
        logger.warn(`[fileStore] Skipping invalid floor appearance mode from ${source}`, {
          value: mode,
        });
      }
      const floorColorHex = floorAppearance.floorColorHex;
      if (typeof floorColorHex === "string" && /^#?[0-9a-f]{6}$/i.test(floorColorHex.trim())) {
        patch.floorColorHex = floorColorHex;
      } else if (floorColorHex == null) {
        patch.floorColorHex = null;
      } else if ("floorColorHex" in floorAppearance) {
        logger.warn(`[fileStore] Skipping invalid floor color from ${source}`, {
          value: floorColorHex,
        });
      }
      const floorOpacity = floorAppearance.floorOpacity;
      const opacityValue = Number(floorOpacity);
      if (floorOpacity == null) {
        patch.floorOpacity = null;
      } else if (Number.isFinite(opacityValue)) {
        patch.floorOpacity = Math.max(0, Math.min(1, opacityValue));
      } else if ("floorOpacity" in floorAppearance) {
        logger.warn(`[fileStore] Skipping invalid floor opacity from ${source}`, {
          value: floorOpacity,
        });
      }
      const floorFinish = floorAppearance.floorFinish;
      const validFloorFinishes = Object.values(FLOOR_FINISHES) as string[];
      if (typeof floorFinish === "string" && validFloorFinishes.includes(floorFinish)) {
        patch.floorFinish = floorFinish;
      } else if (floorFinish == null) {
        patch.floorFinish = null;
      } else if ("floorFinish" in floorAppearance) {
        logger.warn(`[fileStore] Skipping invalid floor finish from ${source}`, {
          value: floorFinish,
        });
      }
      if (Object.keys(patch).length > 0) {
        applyProjectFloorAppearanceOptions(patch);
        logger.info(`[fileStore] Restored floor appearance from ${source}`, patch);
        restoredCount += 1;
      }
    }
  }

  return restoredCount;
}

/** Body labels from config `naming.bodies` (order: label, name, mjcfName). */
const exportedBodyNameCandidatesFromNamingEntry = (entry: NamedUuidConfigEntry): string[] => {
  const out: string[] = [];
  const push = (value: unknown) => {
    if (typeof value !== "string") return;
    const t = value.trim();
    if (t && !out.includes(t)) out.push(t);
  };
  push(entry?.label);
  push(entry?.name);
  push(entry?.mjcfName);
  return out;
};

/**
 * After XML reload, body/site UUIDs change but `cameraProbe.savedCameras` still reference
 * export-time UUIDs. Map mounts using `naming` (old uuid → human / MJCF names) and the
 * current scene maps (names from fresh parse).
 */
function remapCameraProbeSavedMountsFromNaming(
  savedCameras: unknown[] | null | undefined,
  naming: NamingConfig | null | undefined,
  bodies: Record<string, Body> | undefined,
  sites: Record<string, Site> | undefined
): unknown[] {
  if (!Array.isArray(savedCameras) || !savedCameras.length)
    return Array.isArray(savedCameras) ? savedCameras : [];

  const bodyMap = bodies || {};
  const siteMap = sites || {};

  const oldBodyUuidToNames = new Map<string, string[]>();
  if (Array.isArray(naming?.bodies)) {
    for (const b of naming.bodies) {
      if (!b?.uuid) continue;
      const names = exportedBodyNameCandidatesFromNamingEntry(b);
      if (!names.length) continue;
      oldBodyUuidToNames.set(normalizeUuidKey(b.uuid), names);
    }
  }

  const oldSiteUuidToName = new Map<string, string>();
  if (Array.isArray(naming?.sites)) {
    for (const s of naming.sites) {
      if (!s?.uuid || typeof s.name !== "string" || !s.name.trim()) continue;
      oldSiteUuidToName.set(normalizeUuidKey(s.uuid), s.name.trim());
    }
  }

  const findBodyUuidByAnyName = (names: string[]): string | undefined => {
    for (const want of names) {
      for (const body of Object.values(bodyMap)) {
        if (!body || typeof body.uuid !== "string") continue;
        if (String(body.name || "").trim() === want) return body.uuid;
      }
    }
    return undefined;
  };

  const findSiteUuidByName = (want: string): string | undefined => {
    for (const site of Object.values(siteMap)) {
      if (!site || typeof site.uuid !== "string") continue;
      if (String(site.name || "").trim() === want) return site.uuid;
    }
    return undefined;
  };

  return savedCameras.map((raw) => {
    const cameraConfig = raw as SavedCameraConfig;
    if (!raw || String(cameraConfig.kind) !== "mounted") return raw;
    const next: SavedCameraConfig = { ...cameraConfig };

    if (String(next.mountType) === "site") {
      const mountSiteUuid = next.mountSiteUuid;
      if (mountSiteUuid && !recordHasEntityKey(siteMap, mountSiteUuid)) {
        const names = oldSiteUuidToName.get(normalizeUuidKey(mountSiteUuid));
        if (names) {
          const resolved = findSiteUuidByName(names);
          if (resolved) next.mountSiteUuid = resolved;
        }
      }
    } else {
      const mountBodyUuid = next.mountBodyUuid;
      if (mountBodyUuid && !recordHasEntityKey(bodyMap, mountBodyUuid)) {
        const names = oldBodyUuidToNames.get(normalizeUuidKey(mountBodyUuid));
        if (names?.length) {
          const resolved = findBodyUuidByAnyName(names);
          if (resolved) next.mountBodyUuid = resolved;
        }
      }
    }
    return next;
  });
}

function applyCameraOrbitFromConfig(
  cfg: { camera?: CameraConfig; orbit?: OrbitConfig } | null,
  sourceLabel: string
): void {
  if (!cfg) return;

  const toVec3 = (value: unknown): [number, number, number] | null => {
    if (!Array.isArray(value) || value.length !== 3) return null;
    const nums = value.map((v) => Number(v));
    return nums.every((n) => Number.isFinite(n)) ? (nums as [number, number, number]) : null;
  };

  const toQuatXyzw = (value: unknown): [number, number, number, number] | null => {
    if (!Array.isArray(value) || value.length !== 4) return null;
    const nums = value.map((v) => Number(v));
    return nums.every((n) => Number.isFinite(n))
      ? (nums as [number, number, number, number])
      : null;
  };

  const attemptApply = (): boolean => {
    const { camera, controls } = getViewerHandles();
    const cameraCfg = cfg.camera || null;
    const orbitCfg = cfg.orbit || null;

    let appliedAny = false;

    if (camera && cameraCfg) {
      const pos = toVec3(cameraCfg.position);
      const quat = toQuatXyzw(cameraCfg.quaternionXyzw);
      if (pos) {
        camera.position?.set(pos[0], pos[1], pos[2]);
        appliedAny = true;
      }
      if (quat) {
        // THREE.Quaternion is xyzw
        camera.quaternion?.set(quat[0], quat[1], quat[2], quat[3]);
        appliedAny = true;
      }
      const zoom = Number(cameraCfg.zoom);
      if (Number.isFinite(zoom) && zoom > 0) {
        camera.zoom = zoom;
        appliedAny = true;
      }
      const fov = Number(cameraCfg.fov);
      if (Number.isFinite(fov) && fov > 0) {
        camera.fov = fov;
        appliedAny = true;
      }
      const near = Number(cameraCfg.near);
      if (Number.isFinite(near) && near > 0) {
        camera.near = near;
        appliedAny = true;
      }
      const far = Number(cameraCfg.far);
      if (Number.isFinite(far) && far > 0) {
        camera.far = far;
        appliedAny = true;
      }
      if (appliedAny && typeof camera.updateProjectionMatrix === "function") {
        camera.updateProjectionMatrix();
      }
    }

    if (controls && orbitCfg) {
      const target = toVec3(orbitCfg.target);
      if (target && controls.target && typeof controls.target.set === "function") {
        controls.target.set(target[0], target[1], target[2]);
        appliedAny = true;
      }
      if (appliedAny && typeof controls.update === "function") {
        controls.update();
      }
    }

    return appliedAny;
  };

  // Camera/controls may not be ready on the exact tick the scene loads; retry briefly.
  let tries = 0;
  const maxTries = 12;
  const tick = () => {
    tries += 1;
    const ok = attemptApply();
    if (ok) {
      captureCameraHome();
      logger.info("[fileStore] Restored camera/orbit from", sourceLabel);
      return;
    }
    if (tries >= maxTries) {
      logger.warn(
        "[fileStore] Camera/orbit config present but viewer not ready; skipping restore.",
        {
          sourceLabel,
          hasCamera: !!cfg.camera,
          hasOrbit: !!cfg.orbit,
        }
      );
      return;
    }
    setTimeout(tick, 50);
  };
  setTimeout(tick, 0);
}

function applyViewportCameraFromSceneCameras(): void {
  const findViewportCamera = () => {
    const cameras = getSceneState().cameras || {};
    return (
      Object.values(cameras).find(
        (cam) =>
          cam?.kind === "fixed" && String(cam?.name || "").trim() === VIEWPORT_EXPORT_CAMERA_NAME
      ) || null
    );
  };

  const normalizeQuatWxyz = (value: unknown): [number, number, number, number] | null => {
    if (!Array.isArray(value) || value.length !== 4) return null;
    const nums = value.map((v) => Number(v));
    if (!nums.every((n) => Number.isFinite(n))) return null;
    const len = Math.hypot(nums[0], nums[1], nums[2], nums[3]);
    if (len < 1e-8) return null;
    return [nums[0] / len, nums[1] / len, nums[2] / len, nums[3] / len];
  };

  const rotateVecByQuat = (
    q: [number, number, number, number],
    v: [number, number, number]
  ): [number, number, number] => {
    const [w, x, y, z] = q;
    const vx = v[0];
    const vy = v[1];
    const vz = v[2];
    const tx = 2 * (y * vz - z * vy);
    const ty = 2 * (z * vx - x * vz);
    const tz = 2 * (x * vy - y * vx);
    return [
      vx + w * tx + (y * tz - z * ty),
      vy + w * ty + (z * tx - x * tz),
      vz + w * tz + (x * ty - y * tx),
    ];
  };

  const attemptApply = (): boolean => {
    const camDef = findViewportCamera();
    if (!camDef) return true;
    const { camera, controls } = getViewerHandles();
    if (!camera) return false;
    const pos =
      Array.isArray(camDef.position) && camDef.position.length === 3
        ? camDef.position.map((v: unknown) => Number(v))
        : null;
    const quat = normalizeQuatWxyz(camDef.orientation);
    if (!pos || !pos.every((n: number) => Number.isFinite(n)) || !quat) return true;

    camera.position?.set(pos[0], pos[1], pos[2]);
    camera.quaternion?.set(quat[1], quat[2], quat[3], quat[0]);
    const fovy = Number(camDef.fovy);
    if (Number.isFinite(fovy) && fovy > 0) {
      camera.fov = fovy;
      camera.updateProjectionMatrix?.();
    }

    if (controls?.target?.set) {
      const currentDistance =
        typeof camera.position?.distanceTo === "function" && controls.target
          ? Math.max(camera.position.distanceTo(controls.target), 0.1)
          : 2;
      const forward = rotateVecByQuat(quat, [0, 0, -1]);
      controls.target.set(
        pos[0] + forward[0] * currentDistance,
        pos[1] + forward[1] * currentDistance,
        pos[2] + forward[2] * currentDistance
      );
      controls.update?.();
    }
    captureCameraHome();
    return true;
  };

  let tries = 0;
  const maxTries = 12;
  const tick = () => {
    tries += 1;
    if (attemptApply() || tries >= maxTries) return;
    setTimeout(tick, 50);
  };
  setTimeout(tick, 0);
}

const toFiniteArray = (value: unknown, length: number): number[] | null => {
  if (!Array.isArray(value) || value.length !== length) return null;
  const nums = value.map(Number);
  return nums.every((n) => Number.isFinite(n)) ? nums : null;
};

const resolveRecordKeyForPose = (
  record: Record<string, { name?: string } | undefined>,
  uuid: unknown,
  name: unknown
): string | null => {
  const normalizedUuid = normalizeUuidKey(uuid);
  if (normalizedUuid && record[normalizedUuid]) return normalizedUuid;

  const label = typeof name === "string" ? name.trim() : "";
  if (!label) return null;
  const matches = Object.entries(record)
    .filter(([, entry]) => entry && String(entry.name || "").trim() === label)
    .map(([key]) => key);
  return matches.length === 1 ? matches[0] : null;
};

function applyPoseConfigFromSceneConfig(sceneCfg: SceneConfigLike): void {
  const initPose = sceneCfg?.poses?.init;
  if (!initPose || typeof initPose !== "object") return;

  const sceneActions = getSceneActions();
  const sceneState = getSceneState();
  const bodies = (sceneState.bodies || {}) as Record<string, Body>;
  const joints = (sceneState.joints || {}) as Record<string, Joint>;
  let restoredBodies = 0;
  let restoredJoints = 0;

  Object.entries(initPose.bodies || {}).forEach(([uuid, raw]) => {
    const key = resolveRecordKeyForPose(bodies, uuid, raw?.name);
    if (!key) return;
    const position = toFiniteArray(raw?.position, 3);
    const orientation = toFiniteArray(raw?.orientation, 4);
    if (!position || !orientation) return;
    sceneActions.setBodyTransform(key as BodyUUID, position as Vector3, orientation as Quaternion);
    restoredBodies += 1;
  });

  Object.entries(initPose.joints || {}).forEach(([uuid, raw]) => {
    const key = resolveRecordKeyForPose(joints, uuid, raw?.name);
    if (!key) return;
    const joint = joints[key];
    if (!joint) return;
    if (joint.kind === "hinge" || joint.kind === "slide") {
      const value = Number(raw?.value);
      if (!Number.isFinite(value)) return;
      sceneActions.updateJointValue(key, value);
      restoredJoints += 1;
      return;
    }
    if (joint.kind === "ball") {
      const quat = toFiniteArray(raw?.quat, 4);
      if (!quat) return;
      sceneActions.updateBallJointQuat(key, quat as [number, number, number, number]);
      restoredJoints += 1;
    }
  });

  if (restoredBodies > 0 || restoredJoints > 0) {
    try {
      window.dispatchEvent(new Event("structure-changed"));
    } catch {
      // Best-effort UI notification; init pose restoration is already applied.
    }
    logger.info("[fileStore] Restored Critter init pose from scene_config.json", {
      bodies: restoredBodies,
      joints: restoredJoints,
    });
  }
}

// Reads env_config.json and/or scene_config.json from the fileMap and applies matching
// scene-related overrides that are not represented in model.xml (e.g., naming, camera/orbit).
// Also patches live Three.js CSS2DObject labels so joint/site markers update without a full
// scene rebuild.
export async function applySceneConfigFromFileMap(fileMap: FileMap): Promise<void> {
  const keys = Object.keys(fileMap || {});
  const findConfigKey = (baseName: string) => {
    const base = baseName.toLowerCase();
    return (
      keys.find((k) => String(k).toLowerCase() === base) ||
      keys.find((k) => String(k).toLowerCase().endsWith(`/${base}`)) ||
      keys.find((k) => String(k).toLowerCase().endsWith(`\\${base}`)) ||
      null
    );
  };
  const readJsonFromFileMap = async (key: string | null) => {
    if (!key) return null;
    const f = fileMap[key] || null;
    if (!f || typeof f.text !== "function") return null;
    try {
      const txt = await f.text();
      return JSON.parse(txt);
    } catch (err) {
      logger.warn("[fileStore] Failed to parse JSON config from fileMap:", key, err);
      return null;
    }
  };

  const envCfg = await readJsonFromFileMap(findConfigKey("env_config.json"));
  const sceneCfgRaw = await readJsonFromFileMap(findConfigKey("scene_config.json"));
  const sceneCfg = sceneCfgRaw ? migrateSceneConfig(sceneCfgRaw) : null;
  const envConfig = (envCfg || null) as SceneConfigLike | null;
  const sceneConfig = (sceneCfg || null) as SceneConfigLike | null;

  if (sceneConfig?.poses) {
    try {
      applyPoseConfigFromSceneConfig(sceneConfig);
    } catch (err) {
      logger.warn("[fileStore] Failed to restore Critter pose from scene_config.json.", err);
    }
  }

  const cameraOrbitCfg =
    envConfig?.camera || envConfig?.orbit || sceneConfig?.camera || sceneConfig?.orbit;
  const cameraOrbitSource =
    envConfig && (envConfig.camera || envConfig.orbit)
      ? "env_config.json"
      : sceneConfig && (sceneConfig.camera || sceneConfig.orbit)
        ? "scene_config.json"
        : null;
  if (cameraOrbitCfg && cameraOrbitSource) {
    applyCameraOrbitFromConfig(
      {
        camera: cameraOrbitSource === "env_config.json" ? envConfig?.camera : sceneConfig?.camera,
        orbit: cameraOrbitSource === "env_config.json" ? envConfig?.orbit : sceneConfig?.orbit,
      },
      cameraOrbitSource
    );
  } else {
    applyViewportCameraFromSceneCameras();
  }

  const envCameraProbe = envConfig?.cameraProbe || null;
  const sceneCameraProbe = sceneConfig?.cameraProbe || null;
  const cameraProbe = envCameraProbe || sceneCameraProbe || null;
  const namingForCameraMounts = envConfig?.naming || sceneConfig?.naming || null;
  if (cameraProbe && Array.isArray(cameraProbe.savedCameras) && cameraProbe.savedCameras.length) {
    try {
      const sceneActions = getSceneActions();
      const sceneState = getSceneState();
      const remappedSavedCameras = remapCameraProbeSavedMountsFromNaming(
        cameraProbe.savedCameras,
        namingForCameraMounts,
        sceneState.bodies,
        sceneState.sites
      );
      sceneActions.setCameras(remappedSavedCameras as unknown as SceneCamera[]);
      logger.info(
        "[fileStore] Restored Camera Probe presets from",
        envCameraProbe ? "env_config.json" : "scene_config.json",
        { count: cameraProbe.savedCameras.length }
      );
    } catch (err) {
      logger.warn("[fileStore] Failed to restore cameraProbe.savedCameras from config.", err);
    }
  }

  const geomPairings =
    (sceneConfig && Array.isArray(sceneConfig.geomPairings)
      ? sceneConfig.geomPairings
      : envConfig && Array.isArray(envConfig.geomPairings)
        ? envConfig.geomPairings
        : null) || null;
  if (geomPairings && geomPairings.length) {
    try {
      restoreGeomPairingsFromConfig(geomPairings, "scene_config.json");
    } catch (err) {
      logger.warn("[fileStore] Failed to restore geom pairings from scene_config.json.", err);
    }
  }

  const visibilityOverrides = sceneConfig?.visibilityOverrides || null;
  if (visibilityOverrides) {
    try {
      restoreVisibilityOverridesFromConfig(visibilityOverrides, "scene_config.json");
    } catch (err) {
      logger.warn(
        "[fileStore] Failed to restore visibility overrides from scene_config.json.",
        err
      );
    }
  }

  const visibilitySettings = sceneConfig?.visibilitySettings || null;
  if (visibilitySettings) {
    try {
      restoreVisibilitySettingsFromConfig(visibilitySettings, "scene_config.json");
    } catch (err) {
      logger.warn("[fileStore] Failed to restore visibility settings from scene_config.json.", err);
    }
  }

  const envNaming = envConfig?.naming || null;
  const sceneNaming = sceneConfig?.naming || null;
  const naming = envNaming || sceneNaming || null;
  const namingSource = naming ? (envNaming ? "env_config.json" : "scene_config.json") : null;

  if (!naming) return;

  const sceneActions = getSceneActions();
  const appliedCounts: Record<string, number> = {
    bodies: 0,
    worldBodies: 0,
    actuators: 0,
    joints: 0,
    sites: 0,
    meshes: 0,
  };

  if (Array.isArray(naming.bodies) && naming.bodies.length) {
    const labelByUuid = new Map(
      naming.bodies
        .map((b) => {
          if (!b || !b.uuid) return null;
          const raw = typeof b.label === "string" && b.label.trim() ? b.label : b.name;
          const label = typeof raw === "string" ? raw.trim() : "";
          if (!label) return null;
          return [normalizeUuidKey(b.uuid), label] as [string, string];
        })
        .filter((entry): entry is [string, string] => Boolean(entry))
    );

    const sceneState = getSceneState();
    const nextBodies: Record<string, Body> = { ...(sceneState.bodies || {}) };
    Object.entries(nextBodies).forEach(([uuid, body]) => {
      const label = labelByUuid.get(normalizeUuidKey(uuid));
      if (label && body && body.name !== label) {
        nextBodies[uuid] = { ...body, name: label };
        appliedCounts.bodies += 1;
      }
    });

    const explicitWorldNameByUuid =
      Array.isArray(naming.worldBodies) && naming.worldBodies.length
        ? new Map(
            naming.worldBodies
              .filter((wb) => wb && wb.uuid && typeof wb.name === "string" && wb.name.trim())
              .map((wb) => [normalizeUuidKey(wb.uuid), String(wb.name)])
          )
        : null;

    const nextWorldBodies = Array.isArray(sceneState.worldBodies)
      ? sceneState.worldBodies
          .map((wb) => {
            const uuid = typeof wb === "string" ? wb : wb?.uuid;
            if (!uuid) return null;
            const label = labelByUuid.get(normalizeUuidKey(uuid));
            const existingName = typeof wb === "string" ? null : wb?.name;
            const explicit = explicitWorldNameByUuid
              ? explicitWorldNameByUuid.get(normalizeUuidKey(uuid))
              : null;
            const next = {
              uuid: String(uuid) as BodyUUID,
              name: String(explicit || label || existingName || uuid),
            };
            if (typeof wb === "object" && wb?.name && next.name !== wb.name) {
              appliedCounts.worldBodies += 1;
            }
            return next;
          })
          .filter((wb): wb is WorldBody => Boolean(wb))
      : sceneState.worldBodies || [];

    sceneActions.setBodies(nextBodies, nextWorldBodies);
  }

  if (Array.isArray(naming.actuators) && naming.actuators.length) {
    const nameByUuid = new Map(
      naming.actuators
        .filter((a) => a && a.uuid && typeof a.name === "string" && a.name.trim())
        .map((a) => [normalizeUuidKey(a.uuid), String(a.name)])
    );
    const nextActuators: Record<string, Actuator> = { ...(getSceneState().actuators || {}) };
    Object.entries(nextActuators).forEach(([uuid, actuator]) => {
      const friendly = nameByUuid.get(normalizeUuidKey(uuid));
      if (friendly && actuator && actuator.name !== friendly) {
        nextActuators[uuid] = { ...actuator, name: friendly };
        appliedCounts.actuators += 1;
      }
    });
    sceneActions.setActuators(nextActuators);
  }

  if (Array.isArray(naming.joints) && naming.joints.length) {
    const nameByUuid = new Map(
      naming.joints
        .filter((j) => j && j.uuid && typeof j.name === "string" && j.name.trim())
        .map((j) => [normalizeUuidKey(j.uuid), String(j.name)])
    );
    const nextJoints: Record<string, Joint> = { ...(getSceneState().joints || {}) };
    Object.entries(nextJoints).forEach(([uuid, joint]) => {
      const friendly = nameByUuid.get(normalizeUuidKey(uuid));
      if (friendly && joint && joint.name !== friendly) {
        nextJoints[uuid] = { ...joint, name: friendly };
        appliedCounts.joints += 1;
      }
    });
    sceneActions.setJoints(nextJoints);
  }

  if (Array.isArray(naming.sites) && naming.sites.length) {
    const nameByUuid = new Map(
      naming.sites
        .filter((s) => s && s.uuid && typeof s.name === "string" && s.name.trim())
        .map((s) => [normalizeUuidKey(s.uuid), String(s.name)])
    );
    const nextSites: Record<string, Site> = { ...(getSceneState().sites || {}) };
    Object.entries(nextSites).forEach(([uuid, site]) => {
      const friendly = nameByUuid.get(normalizeUuidKey(uuid));
      if (friendly && site && site.name !== friendly) {
        nextSites[uuid] = { ...site, name: friendly };
        appliedCounts.sites += 1;
      }
    });
    sceneActions.setSites(nextSites);
  }

  if (Array.isArray(naming.meshes) && naming.meshes.length) {
    const nameByUuid = new Map(
      naming.meshes
        .filter((m) => m && m.uuid && typeof m.name === "string" && m.name.trim())
        .map((m) => [normalizeUuidKey(m.uuid), String(m.name)])
    );
    const meshUpdates: Record<string, Partial<Mesh>> = {};
    Object.values(getSceneState().meshes || {}).forEach((mesh) => {
      if (!mesh?.uuid) return;
      const friendly = nameByUuid.get(normalizeUuidKey(mesh.uuid));
      if (friendly && mesh.name !== friendly) {
        meshUpdates[String(mesh.uuid)] = { uuid: mesh.uuid, name: friendly };
        appliedCounts.meshes += 1;
      }
    });
    if (Object.keys(meshUpdates).length) {
      sceneActions.upsertMeshes(meshUpdates);
    }
  }

  // Patch live Three.js CSS2DObject label text so joint/site labels in the 3D scene
  // update without requiring a full scene rebuild.
  try {
    const finalJoints = (getSceneState().joints || {}) as Record<string, Joint>;
    const finalActuators = getSceneState().actuators || {};
    const finalSites = (getSceneState().sites || {}) as Record<string, Site>;
    const showActuators = useViewerStore.getState().showActuators;
    const { actuatorDisplayLabelByJointUuid } = indexActuatorsForJointVisualization(
      finalActuators,
      finalJoints
    );
    const { scene } = getViewerHandles();
    if (scene && typeof scene.traverse === "function") {
      scene.traverse((obj) => {
        const labelObj = obj as CssLabelObject;
        if (!labelObj || !labelObj.element || !labelObj.element.className) return;
        if (labelObj.element.className === "joint-label") {
          const uuid = labelObj.userData?.jointUuid || labelObj.parent?.userData?.jointUuid;
          if (uuid) {
            const uuidKey = String(uuid);
            const joint = finalJoints[uuidKey] || finalJoints[normalizeUuidKey(uuidKey)];
            if (!joint?.name) return;
            const label = getJointCssLabelMainText({
              showActuators,
              jointName: joint.name,
              jointUuid: uuidKey,
              actuatorDisplayLabelByJointUuid,
            });
            const textEl =
              typeof labelObj.element.querySelector === "function"
                ? labelObj.element.querySelector(".joint-label-text")
                : null;
            const current = typeof textEl?.textContent === "string" ? textEl.textContent : "";
            if (current !== label) setJointLabelDisplayedName(labelObj.element, label);
          }
        } else if (labelObj.element.className === "site-label") {
          const uuid = labelObj.userData?.siteUuid || labelObj.parent?.userData?.siteUuid;
          if (uuid) {
            const uuidKey = String(uuid);
            const site = finalSites[uuidKey] || finalSites[normalizeUuidKey(uuidKey)];
            if (site?.name && labelObj.element.textContent !== site.name) {
              labelObj.element.textContent = site.name;
            }
          }
        }
      });
    }
    window.dispatchEvent(new Event("structure-changed"));
  } catch (error) {
    logger.warn("[fileStore] Failed to patch live labels after scene config import.", error);
  }

  logger.info("[fileStore] Applied naming overrides from", namingSource, appliedCounts);
}
